import * as React from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { id as localeID } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import {
  dateDariIso, isoDariDate, formatTanggal, parseTanggalID,
  formatBulanSingkat, parseBulanID, hariIniISO, NAMA_BULAN_SINGKAT,
} from "@/lib/tanggal";

/** Kelas input teks dasar — dipakai DatePicker & MonthPicker supaya konsisten
 *  dengan input polos yang sudah dipakai di halaman-halaman lain (h-10,
 *  rounded-lg, border-slate-200), BUKAN gaya default shadcn `<Input>`. */
const KELAS_INPUT =
  "h-10 w-full pl-3 pr-9 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 " +
  "placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-600 " +
  "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50";

const KELAS_TOMBOL_KALENDER =
  "absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md " +
  "text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-50";

/**
 * Pengganti `<input type="date">` yang tetap bisa DIKETIK manual (staf gudang
 * mengetik lebih cepat daripada klik-klik kalender), plus kalender visual
 * (locale id-ID) untuk yang mau klik.
 *
 * KONTRAK API — dibuat semirip mungkin dengan `<input type="date">` supaya
 * migrasi dari input native jadi penggantian mekanis:
 *   <input type="date" value={v} onChange={e => setV(e.target.value)} />
 *   <DatePicker value={v} onChange={setV} />
 *
 * Props:
 *  - value      : string ISO "YYYY-MM-DD" (atau "" / undefined / null)
 *  - onChange   : (isoString) => void — dipanggil dengan "YYYY-MM-DD", atau ""
 *                 saat dikosongkan. TIDAK menerima event seperti input native.
 *  - placeholder: default "dd/mm/yyyy"
 *  - disabled   : bool
 *  - min, max   : string ISO "YYYY-MM-DD" — membatasi tanggal yang bisa DIKLIK
 *                 di kalender (mengetik manual di luar rentang tetap diterima,
 *                 sama lakunya dengan `<input type="date" min max>` native yang
 *                 juga tidak memblokir ketikan).
 *  - className  : diterapkan ke wrapper (bukan ke elemen <input> langsung)
 *  - data-testid: diteruskan ke <input>; tombol kalender & aksi cepat memakai
 *                 turunannya (`${testid}-btn-kalender`, `${testid}-hari-ini`, dst)
 */
const DatePicker = React.forwardRef(function DatePicker(
  { value, onChange, placeholder = "dd/mm/yyyy", disabled = false, min, max, className, "data-testid": testId, ...props },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const [teks, setTeks] = React.useState(() => (value ? formatTanggal(value) : ""));

  // Sinkron teks tampilan saat `value` berubah dari luar (mis. reset form,
  // load data) — tapi jangan timpa selagi popover kalender terbuka.
  React.useEffect(() => {
    if (open) return;
    setTeks(value ? formatTanggal(value) : "");
  }, [value, open]);

  const tanggalTerpilih = dateDariIso(value);
  const tanggalMin = min ? dateDariIso(min) : undefined;
  const tanggalMax = max ? dateDariIso(max) : undefined;

  const commit = (mentah) => {
    const t = mentah.trim();
    if (!t) { onChange?.(""); setTeks(""); return; }
    const iso = parseTanggalID(t);
    if (!iso) {
      // Ketikan tidak valid -> kembalikan tampilan ke nilai terakhir yang sah.
      setTeks(value ? formatTanggal(value) : "");
      return;
    }
    onChange?.(iso);
    setTeks(formatTanggal(iso));
  };

  const pilihTanggal = (iso) => {
    onChange?.(iso);
    setTeks(iso ? formatTanggal(iso) : "");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={teks}
            disabled={disabled}
            onChange={(e) => setTeks(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); }
              if (e.key === "Escape") setOpen(false);
            }}
            data-testid={testId}
            className={KELAS_INPUT}
            {...props}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            data-testid={testId ? `${testId}-btn-kalender` : undefined}
            aria-label="Buka kalender"
            className={KELAS_TOMBOL_KALENDER}
          >
            <CalendarIcon className="w-4 h-4" />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          locale={localeID}
          selected={tanggalTerpilih}
          defaultMonth={tanggalTerpilih}
          onSelect={(d) => d && pilihTanggal(isoDariDate(d))}
          disabled={(d) => {
            if (tanggalMin && d < tanggalMin) return true;
            if (tanggalMax && d > tanggalMax) return true;
            return false;
          }}
          initialFocus
        />
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
          <button
            type="button"
            onClick={() => pilihTanggal(hariIniISO())}
            data-testid={testId ? `${testId}-hari-ini` : undefined}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-800"
          >
            Hari ini
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => pilihTanggal("")}
              data-testid={testId ? `${testId}-bersihkan` : undefined}
              className="text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              Bersihkan
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
});
DatePicker.displayName = "DatePicker";

/**
 * Pengganti `<input type="month">` (dipakai untuk filter periode gajian,
 * target, laporan, dsb). Ketikan manual memakai format "mm/yyyy" — urutan
 * sama seperti dd/mm/yyyy di <DatePicker> supaya staf tidak perlu ingat dua
 * urutan berbeda. Klik ikon kalender membuka grid 12 bulan + navigasi tahun.
 *
 * Props:
 *  - value      : string ISO "YYYY-MM" (atau "" / undefined / null)
 *  - onChange   : (isoBulan) => void — "YYYY-MM", atau "" saat dikosongkan
 *  - placeholder: default "mm/yyyy"
 *  - disabled   : bool
 *  - min, max   : string ISO "YYYY-MM" — membatasi bulan yang bisa DIKLIK
 *  - className  : diterapkan ke wrapper
 *  - data-testid: diteruskan ke <input>; tombol & sel bulan memakai turunannya
 *                 (`${testid}-btn-kalender`, `${testid}-bulan-8`, dst — angka
 *                 1-12)
 */
const MonthPicker = React.forwardRef(function MonthPicker(
  { value, onChange, placeholder = "mm/yyyy", disabled = false, min, max, className, "data-testid": testId, ...props },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const [teks, setTeks] = React.useState(() => formatBulanSingkat(value));
  const [tahunTampil, setTahunTampil] = React.useState(() => {
    const m = /^(\d{4})/.exec(value || "");
    return m ? Number(m[1]) : new Date().getFullYear();
  });

  React.useEffect(() => {
    if (open) return;
    setTeks(formatBulanSingkat(value));
    const m = /^(\d{4})/.exec(value || "");
    if (m) setTahunTampil(Number(m[1]));
  }, [value, open]);

  const commit = (mentah) => {
    const t = mentah.trim();
    if (!t) { onChange?.(""); setTeks(""); return; }
    const iso = parseBulanID(t);
    if (!iso) { setTeks(formatBulanSingkat(value)); return; }
    onChange?.(iso);
    setTeks(formatBulanSingkat(iso));
    setTahunTampil(Number(iso.slice(0, 4)));
  };

  const pilihBulan = (bln) => {
    const iso = `${tahunTampil}-${String(bln).padStart(2, "0")}`;
    onChange?.(iso);
    setTeks(formatBulanSingkat(iso));
    setOpen(false);
  };

  const bulanAktif = value ? Number(value.slice(5, 7)) : null;
  const tahunAktif = value ? Number(value.slice(0, 4)) : null;
  const [minY, minM] = min ? [Number(min.slice(0, 4)), Number(min.slice(5, 7))] : [null, null];
  const [maxY, maxM] = max ? [Number(max.slice(0, 4)), Number(max.slice(5, 7))] : [null, null];
  const diluarRentang = (bln, thn) => {
    if (minY != null && (thn < minY || (thn === minY && bln < minM))) return true;
    if (maxY != null && (thn > maxY || (thn === maxY && bln > maxM))) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
            value={teks}
            disabled={disabled}
            onChange={(e) => setTeks(e.target.value)}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); }
              if (e.key === "Escape") setOpen(false);
            }}
            data-testid={testId}
            className={KELAS_INPUT}
            {...props}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
            data-testid={testId ? `${testId}-btn-kalender` : undefined}
            aria-label="Buka pemilih bulan"
            className={KELAS_TOMBOL_KALENDER}
          >
            <CalendarIcon className="w-4 h-4" />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-56 p-3">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => setTahunTampil((y) => y - 1)}
            data-testid={testId ? `${testId}-tahun-prev` : undefined}
            aria-label="Tahun sebelumnya"
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100 text-slate-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-slate-900">{tahunTampil}</span>
          <button
            type="button"
            onClick={() => setTahunTampil((y) => y + 1)}
            data-testid={testId ? `${testId}-tahun-next` : undefined}
            aria-label="Tahun berikutnya"
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100 text-slate-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {NAMA_BULAN_SINGKAT.map((nama, idx) => {
            const bln = idx + 1;
            const aktif = bulanAktif === bln && tahunAktif === tahunTampil;
            const nonaktif = diluarRentang(bln, tahunTampil);
            return (
              <button
                key={nama}
                type="button"
                disabled={nonaktif}
                onClick={() => pilihBulan(bln)}
                data-testid={testId ? `${testId}-bulan-${bln}` : undefined}
                className={cn(
                  "h-8 rounded-md text-xs font-medium transition-colors",
                  aktif ? "bg-emerald-600 text-white" : "text-slate-700 hover:bg-emerald-50",
                  nonaktif && "opacity-40 pointer-events-none"
                )}
              >
                {nama}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
});
MonthPicker.displayName = "MonthPicker";

export { DatePicker, MonthPicker };
export default DatePicker;
